import { useEffect, useState, useContext, useMemo } from "react";

import {
  ClipboardList,
  Building2,
  CalendarDays,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  Loader2,
  History,
  LayoutGrid,
  List,
  Search,
  TrendingUp,
  Users
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion
} from "firebase/firestore";

import { db } from "../firebase";
import { AuthContext } from "../context/AuthContext";

export default function EmployeeTasks() {

  const { user, orgId, orgSlug } = useContext(AuthContext);
  const navigate = useNavigate();

  const [view, setView] = useState("grid");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [showEmployees, setShowEmployees] = useState(null);
  const [employees, setEmployees] = useState([]);
  
  useEffect(() => {

  if (!orgId) return;

  const unsubEmployees = onSnapshot(
    collection(db, "organizations", orgId, "users"),
    (snap) => {

      const empList = snap.docs
  .map(doc => ({
    id: doc.id,
    ...doc.data()
  }))
  .filter(emp => emp.role === "employee"); // ✅ IMPORTANT

      setEmployees(empList);
    }
  );

  return () => unsubEmployees();

}, [orgId]);

const employeeMap = useMemo(() => {
  const map = {};
  employees.forEach(emp => {
    map[emp.id] = emp.personal?.fullName || emp.email;
  });
  return map;
}, [employees]);

  /* ---------------- FETCH TASKS ---------------- */

  useEffect(() => {

    if (!user?.uid || !orgId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "organizations", orgId, "projects"),
      where("assignedEmployees", "array-contains", user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {

        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        setTasks(data);
        setLoading(false);

      },
      (error) => {
        console.error("Firestore Listener Error:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();

  }, [user, orgId]);


  /* ---------------- SEARCH FILTER ---------------- */

  const filteredTasks = tasks.filter(t => {

    const term = search.toLowerCase();

    return (
      t.clientName?.toLowerCase().includes(term) ||
      t.serviceNeed?.toLowerCase().includes(term) ||
      t.description?.toLowerCase().includes(term)
    );

  });


  /* ---------------- ACCEPT TASK ---------------- */

  const acceptTask = async (taskId) => {

    if (!user?.uid || !orgId) return;

    const task = tasks.find(t => t.id === taskId);

    if (task?.acceptedBy?.some(emp => emp.id === user.uid)) return;

    try {

      const ref = doc(db, "organizations", orgId, "projects", taskId);

      await updateDoc(ref, {
        acceptedBy: arrayUnion({
          id: user.uid,
          name: user.displayName || user.email
        })
      });

    } catch (err) {

      console.error("Accept task error:", err);
      alert("Failed to accept task");

    }

  };


  /* ---------------- HISTORY ---------------- */

  const openHistory = (projectId) => {
    navigate(`/${orgSlug}/project-history/${projectId}`);
  };


  /* ---------------- DESCRIPTION TOGGLE ---------------- */

  const toggleReadMore = (id) => {

    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));

  };


  /* ---------------- LOADING ---------------- */

  if (loading) {

    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="animate-spin text-indigo-600" size={36} />
      </div>
    );

  }


  /* ---------------- UI ---------------- */

  return (

    <div className="min-h-screen bg-slate-50 pb-20">

      {/* HEADER */}

      <div className="bg-white border-b shadow-sm">

        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">

          <div className="h-10 w-10 bg-indigo-600 text-white rounded-lg flex items-center justify-center">
            <ClipboardList size={20}/>
          </div>

          <h1 className="text-xl font-semibold">
            My Tasks
          </h1>

        </div>

      </div>


      <div className="max-w-7xl mx-auto px-6 mt-8 space-y-8">


        {/* SEARCH + VIEW */}

        <div className="flex justify-between items-center">

          <div className="relative w-80">

            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              placeholder="Search tasks..."
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              className="w-full border rounded-xl pl-10 pr-4 py-3 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />

          </div>


          <div className="flex gap-2">

            <button
              onClick={()=>setView("grid")}
              className={`p-2 rounded ${view==="grid"?"bg-indigo-600 text-white":"bg-white border"}`}
            >
              <LayoutGrid size={18}/>
            </button>

            <button
              onClick={()=>setView("list")}
              className={`p-2 rounded ${view==="list"?"bg-indigo-600 text-white":"bg-white border"}`}
            >
              <List size={18}/>
            </button>

          </div>

        </div>


        {/* NO TASK */}

        {filteredTasks.length === 0 && (

          <div className="bg-white shadow rounded-xl p-12 text-center border">

            <ClipboardList
              size={40}
              className="mx-auto text-gray-300 mb-4"
            />

            <p className="text-gray-500 text-lg">
              No tasks assigned yet
            </p>

          </div>

        )}


        {/* TASK LIST */}

        <div className={
          view === "grid"
            ? "grid sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
            : "space-y-4"
        }>


          {filteredTasks.map((task)=>{

            const accepted = task.acceptedBy?.some(
              emp => emp.id === user?.uid
            );

            const text = task.description || "";
            const shortText = text.substring(0,180);

            return(

              <div
                key={task.id}
                className="bg-white border rounded-xl p-6 shadow-sm hover:shadow-md transition"
              >


               {/* HEADER */}

<div className="flex justify-between items-start">

  <div className="flex items-center gap-2 mt-1">

    <p className="text-xs text-gray-500">
      Client: {task.clientName}
    </p>

    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide
      ${
        task.clientStatus === "lead"
          ? "bg-amber-100 text-amber-700"
          : task.clientStatus === "contact"
          ? "bg-blue-100 text-blue-700"
          : task.clientStatus === "client"
          ? "bg-emerald-100 text-emerald-700"
          : "bg-gray-100 text-gray-600"
      }`}
    >
      {task.clientStatus || "Unknown"}
    </span>

  </div>

  <button
    onClick={() => openHistory(task.id)}
    className="text-gray-500 hover:text-indigo-600"
  >
    <History size={18}/>
  </button>

</div>

                {/* DESCRIPTION */}

                <div className="prose prose-sm max-w-none text-gray-700 mt-3">

                  <div
                    dangerouslySetInnerHTML={{
                      __html: expanded[task.id] ? text : shortText
                    }}
                  />

                  {text.length > 180 && (

                    <button
                      onClick={()=>toggleReadMore(task.id)}
                      className="text-indigo-600 text-xs mt-1"
                    >
                      {expanded[task.id] ? "Read Less" : "Read More"}
                    </button>

                  )}

                </div>


                {/* CLIENT INFO */}

                <div className="mt-4 space-y-1 text-sm text-gray-600">

                  {task.clientEmail && (
                    <p className="flex items-center gap-2">
                      <Mail size={14}/>
                      {task.clientEmail}
                    </p>
                  )}

                  {task.clientPhone && (
                    <p className="flex items-center gap-2">
                      <Phone size={14}/>
                      {task.clientPhone}
                    </p>
                  )}

                  {task.clientAddress && (
                    <p className="flex items-center gap-2">
                      <MapPin size={14}/>
                      {task.clientAddress}
                    </p>
                  )}

                </div>


                {/* DUE DATE */}

                <div className="flex items-center gap-2 text-xs text-gray-500 mt-4">
                  <CalendarDays size={14}/>
                  {task.dueDate || "No due date"}
                </div>


                {/* PROGRESS */}

                {/* PROGRESS */}

{task.progress !== undefined && task.progress !== null && (

  <div className="mt-4">

    <div className="flex justify-between text-xs text-gray-500 mb-1">
      <span>Progress</span>
      <span>{Math.min(task.progress,100)}%</span>
    </div>

    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">

      <div
        className="h-full bg-indigo-600 transition-all duration-500"
        style={{
          width:`${Math.min(task.progress,100)}%`
        }}
      />

    </div>

  </div>

)}


                {/* ACTION */}

                <div className="flex justify-between items-center mt-5">

                  <button
  onClick={() => setShowEmployees(task)}
  className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600"
>
  <Users size={14}/>
  {task.assignedEmployees?.length || 0} Employees
</button>


                  {accepted ? (

                    <div className="flex items-center gap-2 text-green-600 text-sm font-medium">

                      <CheckCircle size={18}/>
                      Accepted

                    </div>

                  ) : (

                    <button
                      onClick={()=>acceptTask(task.id)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm"
                    >
                      Accept Task
                    </button>

                  )}

                </div>
                {showEmployees && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

    <div className="bg-white rounded-xl p-6 w-80 shadow-lg">

      <h2 className="text-lg font-semibold mb-4">
        Assigned Employees
      </h2>

      <div className="space-y-2 max-h-60 overflow-y-auto">

        {showEmployees.assignedEmployees?.length > 0 ? (
          showEmployees.assignedEmployees.map((emp, index) => (
            <div
              key={index}
              className="p-2 border rounded-lg text-sm"
            >
              {employeeMap[emp] || emp}
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-sm">
            No employees assigned
          </p>
        )}

      </div>

      <button
        onClick={() => setShowEmployees(null)}
        className="mt-4 w-full bg-indigo-600 text-white py-2 rounded-lg"
      >
        Close
      </button>

    </div>

  </div>
)}

              </div>

            );

          })}

        </div>

      </div>

    </div>

  );

}